// frontend/src/components/CsvUpload/CsvUpload.tsx

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './CsvUpload.css';

interface Column {
    name: string;
    type: string;
    sample: any;
}

interface UploadData {
    filename: string;
    size: number;
    columns: Column[];
    rows: any[];
    totalRows: number;
    totalRowsEstimated: boolean;
    tempPath: string;
}

export const CsvUploadInner: React.FC = () => {
    const navigate = useNavigate();

    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<UploadData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [navegando, setNavegando] = useState(false);

    const getFriendlyError = (err: any, phase: 'upload' | 'confirm'): string => {
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
            return phase === 'upload'
                ? 'Tempo esgotado no envio. Verifique sua conexão e tente novamente.'
                : 'Tempo esgotado ao processar o arquivo. O arquivo é muito grande — tente dividir em partes menores.';
        }

        if (!err.response) {
            return 'Não foi possível contatar o servidor. Verifique sua conexão e tente novamente.';
        }

        if (err.response.status === 413) {
            return err.response.data?.message || 'Arquivo muito grande. O limite é 500 MB.';
        }

        if (err.response.status === 400) {
            return err.response.data?.message || 'Dados inválidos no upload.';
        }

        if (err.response.status === 401) {
            return 'Sessão expirada. Faça login novamente.';
        }

        if (err.response.status === 404) {
            return 'Arquivo não encontrado no servidor. Faça o upload novamente.';
        }

        if (err.response.status === 500) {
            return err.response.data?.message || 'Erro no servidor. Tente novamente em instantes.';
        }

        return err.response.data?.message || err.message || 'Erro inesperado.';
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            setFile(selectedFile);
            setError(null);
            setData(null);
            setSuccessMessage(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Selecione um arquivo CSV');
            return;
        }

        setUploading(true);
        setError(null);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.post('/csv/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 5 * 60 * 1000
            });

            setData(response.data.data);
            setSelectedColumns(response.data.data.columns.map((col: Column) => col.name));
            setSuccessMessage(null);
        } catch (err: any) {
            setError(getFriendlyError(err, 'upload'));
        } finally {
            setUploading(false);
        }
    };

    const handleConfirm = async () => {
        if (!data) return;

        setLoading(true);
        setError(null);

        try {
            const tableName = data.filename.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9_]/g, '_');

            const response = await api.post('/csv/confirm', {
                tempPath: data.tempPath,
                tableName: tableName,
                columns: selectedColumns
            }, {
                timeout: 10 * 60 * 1000
            });

            const totalInserido = response.data.data?.recordCount ?? '?';
            setSuccessMessage(
                `✅ ${totalInserido} registros importados na tabela "${tableName}". ` +
                `Redirecionando para o processamento...`
            );

            setData(null);
            setFile(null);
            setNavegando(true);

            const fileInput = document.getElementById('file-input') as HTMLInputElement;
            if (fileInput) fileInput.value = '';

            setTimeout(() => {
                navigate('/orc', { state: { datasetName: tableName } });
            }, 2000);

        } catch (err: any) {
            setError(getFriendlyError(err, 'confirm'));
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!data) return;

        try {
            await api.post('/csv/cancel', {
                tempPath: data.tempPath
            });
        } catch (err) {
            console.error('Erro ao cancelar:', err);
        }

        setData(null);
        setFile(null);
        const fileInput = document.getElementById('file-input') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
    };

    const toggleColumn = (columnName: string) => {
        setSelectedColumns(prev =>
            prev.includes(columnName)
                ? prev.filter(c => c !== columnName)
                : [...prev, columnName]
        );
    };

    const getTableName = () => {
        if (!file) return '';
        return file.name.replace(/\.[^/.]+$/, '').toLowerCase().replace(/[^a-z0-9_]/g, '_');
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    };

    const getTypeColor = (type: string) => {
        const colors: Record<string, string> = {
            string: '#4fc3f7',
            integer: '#66bb6a',
            float: '#ffa726',
            datetime: '#ab47bc',
            boolean: '#ef5350'
        };
        return colors[type] || '#8899aa';
    };

    return (
        <div className="csv-upload-container">
            <div className="csv-upload-card">
                <h2>📤 Enviar meu arquivo CSV</h2>
                <p className="subtitle">Selecione um arquivo CSV para importar dados em massa</p>

                {file && (
                    <div className="table-info">
                        <span>📋 Tabela destino: <strong>{getTableName()}</strong></span>
                        <span className="table-info-hint">(nome gerado automaticamente a partir do arquivo)</span>
                    </div>
                )}

                <div className="upload-area">
                    <div className="file-input-wrapper">
                        <input
                            id="file-input"
                            type="file"
                            accept=".csv"
                            onChange={handleFileChange}
                            disabled={uploading || loading || navegando}
                        />
                        <label htmlFor="file-input" className="file-label">
                            <span className="icon">📁</span>
                            <span>{file ? file.name : 'Clique para selecionar um arquivo CSV'}</span>
                        </label>
                    </div>

                    {file && (
                        <div className="file-info">
                            <span>📄 {file.name}</span>
                            <span>📊 {formatFileSize(file.size)}</span>
                            <span>🏷️ Tabela: {getTableName()}</span>
                        </div>
                    )}

                    <button
                        className="btn-upload"
                        onClick={handleUpload}
                        disabled={!file || uploading || loading || navegando}
                    >
                        {uploading ? '⏳ Enviando...' : '📤 Enviar'}
                    </button>

                    {uploading && (
                        <p className="upload-hint" style={{ marginTop: 8, fontSize: 13, color: '#8899aa' }}>
                            Processando o arquivo. Isso pode levar alguns segundos.
                        </p>
                    )}
                </div>

                {error && (
                    <div className="error-message">❌ {error}</div>
                )}

                {successMessage && (
                    <div className="success-message" style={{
                        padding: '16px',
                        background: 'rgba(102, 187, 106, 0.1)',
                        border: '1px solid #66bb6a',
                        borderRadius: '8px',
                        color: '#66bb6a',
                        fontSize: '15px',
                        textAlign: 'center',
                        marginTop: '16px'
                    }}>
                        {successMessage}
                    </div>
                )}

                {data && (
                    <div className="preview-section">
                        <div className="preview-header">
                            <h3>📊 Preview dos Dados</h3>
                            <div className="preview-info">
                                <span>Arquivo: {data.filename}</span>
                                <span>Tabela: {getTableName()}</span>
                                <span>Colunas: {data.columns.length}</span>
                                <span>
                                    Registros: {data.totalRowsEstimated
                                        ? `~${data.totalRows.toLocaleString('pt-BR')} (estimado)`
                                        : data.totalRows.toLocaleString('pt-BR')}
                                </span>
                            </div>
                        </div>

                        <div className="columns-section">
                            <h4>📋 Selecione as colunas para importar:</h4>
                            <div className="columns-grid">
                                {data.columns.map((col) => (
                                    <label key={col.name} className="column-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={selectedColumns.includes(col.name)}
                                            onChange={() => toggleColumn(col.name)}
                                        />
                                        <span className="column-name">{col.name}</span>
                                        <span
                                            className="column-type"
                                            style={{ backgroundColor: getTypeColor(col.type) }}
                                        >
                                            {col.type}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>

                        <div className="table-section">
                            <h4>📊 Primeiros 5 Registros</h4>
                            <div className="table-responsive">
                                <table className="preview-table">
                                    <thead>
                                        <tr>
                                            {data.columns.map((col) => (
                                                <th key={col.name}>{col.name}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.rows.map((row, idx) => (
                                            <tr key={idx}>
                                                {data.columns.map((col) => (
                                                    <td key={col.name}>
                                                        {row[col.name] !== undefined && row[col.name] !== null
                                                            ? String(row[col.name]).substring(0, 50)
                                                            : '-'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="actions-section">
                            <div className="action-buttons">
                                <button
                                    className="btn-confirm"
                                    onClick={handleConfirm}
                                    disabled={loading || selectedColumns.length === 0 || navegando}
                                >
                                    {loading ? '⏳ Inserindo...' : '✅ Confirmar Inserção'}
                                </button>
                                <button
                                    className="btn-cancel"
                                    onClick={handleCancel}
                                    disabled={loading || navegando}
                                >
                                    ❌ Cancelar
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CsvUploadInner;